// src/ui/screens/chats/ArchiveScreen.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { chatService } from '../../../services/chat/chatService';
import { chatActionsService } from '../../../services/chat/chatActionsService';
import { chatLocalDb } from '../../../services/chat/chatLocalDb';
import { ConversationTile } from './widgets/ConversationTile';
import { db } from '../../../firebase';
import {
  collection,
  getDocs,
  query,
  where,
  doc,
} from 'firebase/firestore';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
  red: '#FF3B30',
  blue: '#4DA3FF',
};

// ── ArchiveScreen ────────────────────────────────────────────────────────────

export const ArchiveScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [archived, setArchived] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [mutedState, setMutedState] = useState({});
  const [blockedState, setBlockedState] = useState({});

  const scrollRef = useRef(null);

  const currentUserId = user?.uid || '';

  // ── Format time ────────────────────────────────────────────────────────────

  const formatTime = (date) => {
    if (!date) return '';
    const dt = date instanceof Date ? date : new Date(date);
    const now = new Date();
    const isToday = dt.getDate() === now.getDate() &&
      dt.getMonth() === now.getMonth() &&
      dt.getFullYear() === now.getFullYear();
    
    if (isToday) {
      let hours = dt.getHours();
      const minutes = dt.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      return `${hours}:${minutes} ${ampm}`;
    }
    
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (dt.getDate() === yesterday.getDate() &&
        dt.getMonth() === yesterday.getMonth() &&
        dt.getFullYear() === yesterday.getFullYear()) {
      return 'Yesterday';
    }
    
    const diffDays = Math.floor((now - dt) / (1000 * 60 * 60 * 24));
    if (diffDays < 7) {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      return days[dt.getDay()];
    }
    
    return `${dt.getDate()}/${dt.getMonth() + 1}/${dt.getFullYear()}`;
  };

  // ── Enrich with user info ─────────────────────────────────────────────────

  const enrichWithUserInfo = useCallback(async (conversations) => {
    const unknownIds = conversations
      .filter(c => c.otherUserId && !c.otherUserName)
      .map(c => c.otherUserId)
      .filter(id => id);

    if (unknownIds.length === 0) return conversations;

    const nameMap = {};
    const avatarMap = {};

    const chunkSize = 30;
    for (let i = 0; i < unknownIds.length; i += chunkSize) {
      const chunk = unknownIds.slice(i, i + chunkSize);
      try {
        const q = query(
          collection(db, 'users'),
          where('uid', 'in', chunk)
        );
        const snap = await getDocs(q);
        for (const doc of snap.docs) {
          const data = doc.data();
          nameMap[doc.id] = data.name || data.displayName || data.username || 'Unknown';
          avatarMap[doc.id] = data.profileImage || data.photoUrl || null;
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    }

    return conversations.map(c => {
      if (nameMap[c.otherUserId]) {
        return {
          ...c,
          otherUserName: nameMap[c.otherUserId],
          otherUserAvatar: avatarMap[c.otherUserId],
        };
      }
      return c;
    });
  }, []);

  // ── Sync states ────────────────────────────────────────────────────────────

  const syncStates = useCallback(async (conversations) => {
    const muted = {};
    const blocked = {};
    for (const c of conversations) {
      try {
        muted[c.id] = await chatActionsService.isMuted(c.id);
        if (c.otherUserId) {
          blocked[c.otherUserId] = await chatActionsService.isBlocked(c.otherUserId);
        }
      } catch (error) {
        console.error('Error syncing states:', error);
      }
    }
    setMutedState(muted);
    setBlockedState(blocked);
  }, []);

  // ── Load archived conversations ───────────────────────────────────────────

  useEffect(() => {
    const loadArchived = async () => {
      setIsLoading(true);
      try {
        // Load from local cache first
        const cached = await chatLocalDb.loadArchivedConversations();
        if (cached && cached.length > 0) {
          const enriched = await enrichWithUserInfo(cached);
          await syncStates(enriched);
          setArchived(enriched);
        }

        // Subscribe to real-time updates
        // In a real implementation, this would be a Firestore subscription
        // For now, we'll use a mock interval
        const interval = setInterval(async () => {
          // Mock update - in real implementation, this would be a real subscription
        }, 5000);

        setIsLoading(false);
        return () => clearInterval(interval);
      } catch (error) {
        console.error('Error loading archived:', error);
        setIsLoading(false);
      }
    };
    loadArchived();
  }, [enrichWithUserInfo, syncStates]);

  // ── Selection handlers ────────────────────────────────────────────────────

  const enterSelection = (firstId) => {
    setIsSelectionMode(true);
    setSelectedIds(new Set([firstId]));
  };

  const exitSelection = () => {
    setIsSelectionMode(false);
    setSelectedIds(new Set());
  };

  const toggleSelection = (id) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
      if (newSet.size === 0) {
        setIsSelectionMode(false);
      }
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const getSelected = () => {
    return archived.filter(c => selectedIds.has(c.id));
  };

  // ── Unarchive ─────────────────────────────────────────────────────────────

  const unarchiveSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    try {
      await chatActionsService.unarchiveMultiple(ids);
      setArchived(prev => prev.filter(c => !ids.includes(c.id)));
      showSnack(
        ids.length === 1 ? 'Conversation unarchived' : `${ids.length} conversations unarchived`
      );
    } catch (error) {
      console.error('Unarchive error:', error);
      showSnack('Failed to unarchive', true);
    }
    exitSelection();
  };

  const unarchiveSingle = async (c) => {
    try {
      await chatActionsService.unarchiveConversation(c.id);
      setArchived(prev => prev.filter(x => x.id !== c.id));
      showSnack('Conversation unarchived');
    } catch (error) {
      console.error('Unarchive error:', error);
      showSnack('Failed to unarchive', true);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const deleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    const confirmed = window.confirm(
      ids.length === 1
        ? 'Delete this conversation? This cannot be undone.'
        : `Delete ${ids.length} conversations? This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      setArchived(prev => prev.filter(c => !ids.includes(c.id)));
      for (const id of ids) {
        await chatLocalDb.markConversationDeleted(id);
      }
      const participantsMap = {};
      for (const c of archived.filter(c => ids.includes(c.id))) {
        participantsMap[c.id] = [currentUserId, c.otherUserId];
      }
      await chatActionsService.deleteMultiple(ids, participantsMap);
      showSnack(
        ids.length === 1 ? 'Conversation deleted' : `${ids.length} conversations deleted`
      );
    } catch (error) {
      console.error('Delete error:', error);
      showSnack('Failed to delete', true);
      // Reload archived
      const recovered = await chatLocalDb.loadArchivedConversations();
      if (recovered) {
        const enriched = await enrichWithUserInfo(recovered);
        setArchived(enriched);
      }
    }
    exitSelection();
  };

  const deleteSingle = async (c) => {
    const confirmed = window.confirm('Delete this conversation? This cannot be undone.');
    if (!confirmed) return;

    try {
      setArchived(prev => prev.filter(x => x.id !== c.id));
      await chatLocalDb.markConversationDeleted(c.id);
      await chatActionsService.deleteConversation(c.id, [currentUserId, c.otherUserId]);
      showSnack('Conversation deleted');
    } catch (error) {
      console.error('Delete error:', error);
      showSnack('Failed to delete', true);
    }
  };

  // ── Snack helper ──────────────────────────────────────────────────────────

  const showSnack = (message, isError = false) => {
    // In a real implementation, use a toast/snackbar system
    console.log(message);
  };

  // ── Render empty state ────────────────────────────────────────────────────

  const renderEmpty = (topPad) => {
    const headerHeight = topPad + 12 + 38 + 14 + 14;
    return (
      <div className="flex items-center justify-center h-full" style={{ paddingTop: headerHeight }}>
        <div className="flex flex-col items-center gap-5">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center border border-white/6"
            style={{ backgroundColor: COLORS.card }}
          >
            <svg className="w-9 h-9 text-white/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
          </div>
          <p className="text-white text-lg font-semibold">No archived chats</p>
          <p className="text-white/35 text-sm text-center">Archived conversations will appear here.</p>
        </div>
      </div>
    );
  };

  // ── Render list ───────────────────────────────────────────────────────────

  const renderList = (topPad) => {
    const headerHeight = topPad + 12 + 38 + 28;

    return (
      <div
        ref={scrollRef}
        className="h-full overflow-y-auto"
        style={{ paddingTop: headerHeight, paddingBottom: isSelectionMode ? 100 : 32 }}
      >
        {archived.map((c) => {
          const isMuted = mutedState[c.id] || false;
          const isBlocked = blockedState[c.otherUserId] || false;
          const isSelected = selectedIds.has(c.id);

          return (
            <ConversationTile
              key={c.id}
              name={c.otherUserName || 'Loading…'}
              lastMessage={isBlocked ? '🚫 Blocked' : (c.lastMessage || 'Say hello! 👋')}
              time={formatTime(c.lastMessageTime)}
              avatarUrl={c.otherUserAvatar}
              unreadCount={isMuted ? 0 : (c.unreadCount || 0)}
              isPinned={false}
              isMuted={isMuted}
              isBlocked={isBlocked}
              isSelectionMode={isSelectionMode}
              isSelected={isSelected}
              onSelectionToggle={() => toggleSelection(c.id)}
              onLongPress={() => {
                if (isSelectionMode) {
                  toggleSelection(c.id);
                } else {
                  enterSelection(c.id);
                }
              }}
              onTap={async () => {
                if (isSelectionMode) {
                  toggleSelection(c.id);
                  return;
                }
                await chatLocalDb.markConversationRead(c.id);
                await chatActionsService.markAsRead(c.id);
                navigate(`/chat/${c.id}`, {
                  state: {
                    receiverId: c.otherUserId,
                    title: c.otherUserName || '',
                    avatarUrl: c.otherUserAvatar,
                  },
                });
              }}
              onSwipeLeft={() => deleteSingle(c)}
              onSwipeRight={() => unarchiveSingle(c)}
            />
          );
        })}
      </div>
    );
  };

  // ── Render selection bar ─────────────────────────────────────────────────

  const renderSelectionBar = () => {
    const count = selectedIds.size;
    if (!isSelectionMode || count === 0) return null;

    return (
      <div className="absolute bottom-4 left-4 right-4 z-10">
        <div
          className="px-1.5 py-1.5 rounded-xl flex items-center"
          style={{
            backgroundColor: `${COLORS.card}EA`,
            backdropFilter: 'blur(24px)',
            border: '0.8px solid rgba(255,255,255,0.1)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          }}
        >
          <div
            className="px-2.5 py-1 rounded-full ml-2"
            style={{ backgroundColor: `${COLORS.orange}26` }}
          >
            <span className="text-filatom-primary text-xs font-semibold">{count}</span>
          </div>

          <div className="flex-1" />

          {/* Unarchive */}
          <button
            onClick={unarchiveSelected}
            className="px-4 py-2.5 rounded-xl flex items-center gap-1.5"
            style={{ backgroundColor: `${COLORS.blue}1F`, border: `0.8px solid ${COLORS.blue}40` }}
          >
            <svg className="w-4.5 h-4.5" fill="none" stroke={COLORS.blue} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
            <span className="text-filatom-blue text-xs font-semibold">Unarchive</span>
          </button>

          {/* Delete */}
          <button
            onClick={deleteSelected}
            className="px-4 py-2.5 rounded-xl flex items-center gap-1.5 ml-2"
            style={{ backgroundColor: `${COLORS.red}1F`, border: `0.8px solid ${COLORS.red}40` }}
          >
            <svg className="w-4.5 h-4.5" fill="none" stroke={COLORS.red} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            <span className="text-red-500 text-xs font-semibold">Delete</span>
          </button>
        </div>
      </div>
    );
  };

  // ── Main render ───────────────────────────────────────────────────────────

  const topPad = 44; // approximate status bar height

  return (
    <div className="min-h-screen bg-filatom-bg relative">
      {/* Header */}
      <div
        className="fixed top-0 left-0 right-0 z-10 px-4 pb-3.5"
        style={{
          paddingTop: topPad + 12,
          backgroundColor: `${COLORS.bg}CC`,
          backdropFilter: 'blur(24px)',
          borderBottom: '0.5px solid rgba(255,255,255,0.06)',
        }}
      >
        <div className="flex items-center">
          {/* Back button */}
          <button
            onClick={() => navigate(-1)}
            className="w-9.5 h-9.5 rounded-full flex items-center justify-center"
            style={{
              backgroundColor: 'rgba(35,38,44,0.55)',
              border: '0.8px solid rgba(255,255,255,0.1)',
            }}
          >
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="flex-1 ml-3.5">
            <p className="text-white text-xl font-bold tracking-tight">Archived</p>
            {archived.length > 0 && (
              <p className="text-white/40 text-xs font-medium">
                {archived.length} {archived.length === 1 ? 'chat' : 'chats'}
              </p>
            )}
          </div>

          {archived.length > 0 && (
            <button
              onClick={isSelectionMode ? exitSelection : () => enterSelection(archived[0].id)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border ${
                isSelectionMode
                  ? 'text-white/55 border-white/10'
                  : 'text-filatom-primary border-filatom-primary/25'
              }`}
              style={{ backgroundColor: isSelectionMode ? 'transparent' : `${COLORS.orange}1F` }}
            >
              {isSelectionMode ? 'Cancel' : 'Edit'}
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="h-screen">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-8 h-8 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : archived.length === 0 ? (
          renderEmpty(topPad)
        ) : (
          renderList(topPad)
        )}
      </div>

      {/* Selection Bar */}
      {renderSelectionBar()}
    </div>
  );
};

export default ArchiveScreen;